import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserMinus, MessageSquareQuote } from "lucide-react";

const REASON_LABELS = {
  too_expensive: "Zu teuer",
  not_enough_use: "Nicht genug Aufträge",
  missing_feature: "Funktion fehlt",
  bug_or_problem: "Bug / Problem",
  found_alternative: "Andere Software",
  just_testing: "Nur zum Testen",
  other: "Anderer Grund",
  no_reason: "Kein Grund angegeben",
};

export default function ChurnInsightsWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/churn-insights")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data || data.total === 0) return null;

  const maxCount = Math.max(...data.reasons.map((r) => r.count), 1);

  return (
    <div className="mt-8 border border-zinc-200 bg-white p-5" data-testid="churn-insights-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 bg-rose-50 text-rose-700 flex items-center justify-center">
          <UserMinus className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <div>
          <div className="font-display font-bold text-zinc-900">Churn-Insights</div>
          <div className="text-xs text-zinc-500">
            {data.total} Konto-Löschung{data.total === 1 ? "" : "en"}
            {data.avg_days_active != null && ` · Ø ${data.avg_days_active} Tage aktiv`}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div data-testid="churn-reason-distribution">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">
            Gründe
          </div>
          <div className="space-y-2">
            {data.reasons.map((r) => (
              <div key={r.reason} data-testid={`churn-reason-${r.reason}`}>
                <div className="flex items-center justify-between text-sm mb-0.5">
                  <span className="text-zinc-700 truncate max-w-[70%]">{REASON_LABELS[r.reason] || r.reason}</span>
                  <span className="font-semibold text-zinc-900">{r.count}</span>
                </div>
                <div className="h-1.5 bg-zinc-100 overflow-hidden">
                  <div
                    className="h-full bg-rose-400"
                    style={{ width: `${(r.count / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {data.comments.length > 0 && (
          <div data-testid="churn-comments">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">
              Letzte Kommentare
            </div>
            <ul className="space-y-2">
              {data.comments.slice(0, 5).map((c, i) => (
                <li
                  key={i}
                  className="border border-zinc-100 bg-zinc-50/60 px-3 py-2 text-sm"
                  data-testid={`churn-comment-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <MessageSquareQuote className="h-3.5 w-3.5 text-zinc-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-zinc-700 break-words line-clamp-3">{c.comment}</p>
                      <div className="text-[11px] text-zinc-400 mt-0.5">
                        {REASON_LABELS[c.reason] || c.reason || "—"}
                        {c.days_active != null && ` · ${c.days_active} Tage aktiv`}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
